import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { signIn } from "@/lib/auth-client";
import { DEV_ADMIN_PASSWORD, DEV_USER_PASSWORD } from "@/lib/dev-accounts";
import { devKeys } from "@/lib/query";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

const isDev = process.env.NODE_ENV !== "production";

const seedAccountsSchema = z.object({
  admin: z.object({ email: z.string().email() }),
  user: z.object({ email: z.string().email() }),
});

type DevAccount = { label: string; email: string; password: string };

// Emails come from a dev-only server endpoint so they always match the seeded
// rows, regardless of which host the studio opened the app at.
function useDevAccounts() {
  return useQuery({
    queryKey: devKeys.seedAccounts,
    enabled: isDev,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<DevAccount[]> => {
      const res = await fetch("/api/dev/seed-accounts");
      if (!res.ok) return [];
      const parsed = seedAccountsSchema.parse(await res.json());
      return [
        { label: "Admin", email: parsed.admin.email, password: DEV_ADMIN_PASSWORD },
        { label: "User", email: parsed.user.email, password: DEV_USER_PASSWORD },
      ];
    },
  });
}

export function LoginForm() {
  const router = useRouter();
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const devAccounts = useDevAccounts().data ?? [];

  function fillCredentials(email: string, password: string) {
    form.setValue("email", email, { shouldValidate: true });
    form.setValue("password", password, { shouldValidate: true });
  }

  async function onSubmit(values: LoginValues) {
    const { error } = await signIn.email({
      email: values.email,
      password: values.password,
    });

    if (error) {
      toast.error(error.message ?? "Failed to sign in");
      return;
    }

    router.navigate({ to: "/dashboard" });
  }

  return (
    <Card className="border-border/50 shadow-sm">
      <CardHeader className="space-y-1 text-center pb-2">
        <h1 className="font-display text-3xl font-bold">Sign In Now!</h1>
        <p className="text-muted-foreground text-sm">Enter your credentials to continue</p>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </Form>
        {isDev && devAccounts.length > 0 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">Sign In As:</span>
            {devAccounts.map((account) => (
              <Button
                key={account.label}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fillCredentials(account.email, account.password)}
              >
                {account.label}
              </Button>
            ))}
          </div>
        )}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Don't have an account?{" "}
          <a href="/signup" className="text-primary hover:underline underline-offset-4">
            Sign up
          </a>
        </p>
      </CardContent>
    </Card>
  );
}
